import {
  createContext,
  type ReactNode,
  useCallback,
  useState,
  useRef,
} from 'react';
import without from 'lodash/without';

import { type IBulkActionsContextValue } from './bulkActionsContext.interfaces';

const defaultContextValue = {
  selected: [],
  unselected: [],
  isAllSelected: false,
  filter: {},
  selectedSingle: () => {},
  setIsAllSelected: () => {},
  checkIsSelected: () => {},
  resetState: () => {},
  setFilter: () => {},
};

const BulkActionsContext =
  // @ts-ignore
  createContext<IBulkActionsContextValue>(defaultContextValue);

const BulkActionsProvider = ({ children }: { children: ReactNode }) => {
  const [selected, setSelected] = useState<
    IBulkActionsContextValue['selected']
  >(defaultContextValue.selected);
  const [unselected, setUnselected] = useState<
    IBulkActionsContextValue['unselected']
  >(defaultContextValue.selected);
  const mediaMapRef = useRef({});

  const [isAllSelected, setIsAllSelected] = useState(
    defaultContextValue.isAllSelected
  );
  const [filter, setFilter] = useState<IBulkActionsContextValue['filter']>({});
  const [allSelectedTotal, setAllSelectedTotal] =
    useState<IBulkActionsContextValue['allSelectedTotal']>(0);

  const selectSingle = useCallback(
    (id: string, value: boolean): void => {
      if (value) {
        setSelected((prev) => [...prev, id]);
        setUnselected((prev) => without(prev, id));
      } else {
        setUnselected((prev) => [...prev, id]);
        setSelected((prev) => without(prev, id));
      }
    },
    [setSelected, setUnselected]
  );

  const setIsAllSelectedSpy = useCallback(
    (value: boolean): void => {
      setUnselected([]);
      setSelected([]);
      setIsAllSelected(value);
    },
    [setIsAllSelected, setSelected, setUnselected]
  );

  const resetState = useCallback((): void => {
    setUnselected([]);
    setSelected([]);
    setIsAllSelected(false);
    setFilter({});
    setAllSelectedTotal(0);
  }, [setAllSelectedTotal, setSelected, setIsAllSelected, setFilter]);

  const checkIsSelected = useCallback(
    (id: string) => {
      return isAllSelected ? !unselected.includes(id) : selected.includes(id);
    },
    [isAllSelected, selected, unselected]
  );

  return (
    <BulkActionsContext.Provider
      value={{
        selected,
        unselected,
        isAllSelected,
        selectSingle,
        checkIsSelected,
        setIsAllSelected: setIsAllSelectedSpy,
        resetState,
        filter,
        setFilter,
        allSelectedTotal,
        setAllSelectedTotal,
        mediaMapRef,
      }}
    >
      {children}
    </BulkActionsContext.Provider>
  );
};

export { BulkActionsContext, BulkActionsProvider };
